<template>
  <div class="d-flex flex-column gap-2" data-testid="yard-task-check-checklist">
    <div
      class="d-flex flex-row align-center gap-1 yard-task-check-checklist-item"
      v-for="(item, index) in checklist"
      :key="item.id">
      <span class="typography-body-sm-bold">{{ index + 1 }}.</span>

      <v-text-field
        color="primary"
        variant="outlined"
        hide-details="auto"
        :disabled="disabled"
        :id="`checklist-item-${index}`"
        :model-value="item.description"
        :rules="[value => validateItem(index, value)]"
        @update:model-value="updateItem(index, $event)"></v-text-field>

      <v-btn
        v-if="checklist.length > 1 && !disabled"
        icon="mdi-trash-can-outline"
        size="small"
        variant="plain"
        @click="removeItem(index)"></v-btn>
    </div>

    <div class="d-flex flex-row align-center gap-1" v-if="!disabled">
      <button type="button" @click="addItem" class="typography-body-sm-bold text--color-text-link">
        + Add item
      </button>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';

import { isChecklistItemDescriptionValid } from '@/modules/yard/components/YardTasksCreateTaskCheckChecklist.helpers';

function generateId() {
  return `checklist-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
}

export default defineComponent({
  name: 'YardTasksCreateTaskCheckChecklist',
  emits: ['update:modelValue', 'requestValidate'],
  components: {},
  props: {
    modelValue: {
      type: Array,
      required: false,
      default: null
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  setup(props, { emit }) {
    const { t } = useI18n();

    const checklist = ref(
      (props.modelValue ?? [{ description: '' }]).map(item => ({
        id: generateId(),
        ...item
      }))
    );

    const addItem = () => {
      checklist.value = [
        ...checklist.value,
        {
          id: generateId(),
          description: ''
        }
      ];

      // focus on the new item
      nextTick(() => {
        document.getElementById(`checklist-item-${checklist.value.length - 1}`)?.focus();
      });
    };

    const removeItem = index => {
      checklist.value = checklist.value.filter((_, i) => i !== index);

      // focus on the previous item
      if (index > 0) {
        nextTick(() => {
          document.getElementById(`checklist-item-${index - 1}`)?.focus();
        });
      }
    };

    const updateItem = (index, value) => {
      checklist.value = [
        ...checklist.value.slice(0, index),
        { ...checklist.value[index], description: value },
        ...checklist.value.slice(index + 1)
      ];
    };

    const validateItem = (index, value) => {
      return (
        isChecklistItemDescriptionValid(checklist.value.length, index, value) ||
        t('yard.drawers.createTaskDrawer.errors.checklistItemDescriptionRequired')
      );
    };

    watch([checklist], ([newChecklist]) => {
      emit('update:modelValue', newChecklist);
      emit('requestValidate');
    });

    return {
      t,
      checklist,
      addItem,
      removeItem,
      updateItem,
      validateItem
    };
  }
});
</script>
